import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import numeral from 'numeral';
import moment from 'moment-timezone';
import {
  Briefcase,
  CalendarDays,
  CalendarPlus,
  FileText,
  GripVertical,
  Hourglass,
  MapPin,
  Send,
  Stethoscope,
  UserRound,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import HoverTip from '../../../common/HoverTip';

// Same card as Client Onboarding (Nic 2026-10-09: "more premium").
const CARD =
  'rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10';

// Submissions on the claim (Nic 2026-10-20: the tile shows how many, not
// the task checklist). The history is an array of { submittedAt, types };
// older rows can hold a single object or a JSON string.
const SUBMISSION_LABELS = {
  new_increase: 'New/Increase',
  supplemental: 'Supplemental',
  hlr: 'HLR',
  bdd: 'BDD',
};
export const submissionsOf = (raw) => {
  let h = raw;
  if (typeof h === 'string') {
    try {
      h = JSON.parse(h);
    } catch {
      return [];
    }
  }
  if (!h) return [];
  return (Array.isArray(h) ? h : [h]).filter((e) => e && typeof e === 'object');
};
const submissionLine = (e) => {
  const types = Array.isArray(e.types) ? e.types : e.types ? [e.types] : [];
  const label = types.map((t) => SUBMISSION_LABELS[t] || t).join(' + ') || 'Submission';
  return e.submittedAt ? `${label} (${moment(e.submittedAt).format('MMM D, YYYY')})` : label;
};

// quiet: no tooltips while another card is being dragged over this one.
// keep: a click on it stays put instead of opening the claim (the small
// icon chips, Nic 2026-10-22).
const Tip = ({ text, quiet, className, keep = false, children }) => {
  const tip = (
    <HoverTip text={text} disabled={quiet} className={className}>
      {children}
    </HoverTip>
  );
  return keep ? (
    <span data-keep-tile="" className="contents">
      {tip}
    </span>
  ) : (
    tip
  );
};

// A click anywhere on the tile opens the claim (Nic 2026-10-22), except on
// the drag handle, the icon chips and the meeting button.
const KEEP = '[data-keep-tile], a, button';

// No meeting invite from Invoiced on (Nic 2026-10-22): the work is done.
export const NO_MEETING_STATUSES = ['invoiced', 'payment plan', 'paid', 'dispute hold', 'dead'];

const SalesCard = ({
  card,
  handleProps,
  className,
  onMeetingInvite = null,
  isAdmin,
  canDrag = true,
  quiet = false,
}) => {
  const navigate = useNavigate();
  const claimPath = `/admin/profile/${card.claimId}`;
  const openClaim = (e) => {
    if (e.target.closest(KEEP)) return;
    // Selecting text on the tile is not a click to open it.
    if (window.getSelection?.().toString()) return;
    navigate(claimPath);
  };

  const daysSince = moment().diff(moment(card.claimUpdatedOn), 'days');
  const financialDisplay = card._actualValue
    ? numeral(card._actualValue).format('$0,0')
    : `${numeral(card._minValue).format('$0,0')} - ${numeral(
        card._maxValue,
      ).format('$0,0')}`;

  const statusKey = String(card.claimStatus || '').toLowerCase();
  const entryLane =
    statusKey === 'lead' ||
    statusKey === 'leads' ||
    statusKey === 'intake' ||
    statusKey === 'awaiting medical records';
  // "Claim Manager" only earns its label when a real CM (claims_manager /
  // claims_admin) holds the claim. Entry lanes always show Onboarding as
  // before; later lanes fall back to the Onboarding line ONLY when the
  // assignee isn't actually a CM (an onboarding stand-in, a director) — a
  // tile calling Jorge "Claim Manager" told the same lie the profile card
  // did (Nic, 2026-08-03). That fallback renders in red: it doubles as the
  // "this claim still needs a Claims Manager" flag.
  const assignedIsRealCm = ['claims_manager', 'claims_admin'].includes(
    card.assignedToUserType,
  );
  const showOnboarding = entryLane || !assignedIsRealCm;
  const needsCmFlag = !entryLane && !assignedIsRealCm;

  // Surface the address indicator only when the client is on the gated
  // population (requireAddress = true) AND their address is not verified.
  // Restrict to entry-stage lanes where the gate fires; later lanes don't
  // need the cue.
  const showAddressMissingBadge =
    card._clientRequireAddress === true &&
    card._clientAddressVerified === false &&
    (statusKey === 'intake' || statusKey === 'awaiting medical records');

  const roleLabel = showOnboarding ? 'Onboarding' : 'Claim Manager';
  const RoleIcon = showOnboarding ? UserRound : Briefcase;

  const roleAssignedId = showOnboarding
    ? card.onboardingAssignedUserId || card.onboardingAssignedToUserId
    : card.assignedToUserId || card.claimAssignedToUserId;

  const roleAssignedName = showOnboarding
    ? `${card.onboardingAssignedUserFirstName || ''} ${
        card.onboardingAssignedUserLastName || ''
      }`.trim()
    : `${card.assignedToUserFirstName || ''} ${card.assignedToUserLastName || ''}`.trim();

  const roleDisplayName = roleAssignedId
    ? roleAssignedName || 'Assigned'
    : 'Unassigned';
  const roleWarn = needsCmFlag || !roleAssignedId;
  const stale = daysSince >= 14;
  const submissions = submissionsOf(card.claimSubmissionHistory);
  const providerName = String(card._providerName || '').trim();

  return (
    <div
      onClick={openClaim}
      className={cn(
        CARD,
        'group flex cursor-pointer flex-col gap-2 px-3.5 py-3 transition-[box-shadow]',
        'ring-1 ring-transparent',
        // No hover look on the other tiles while one is dragged over them
        // (Nic 2026-10-22).
        !quiet &&
          'hover:shadow-[0_1px_2px_rgba(15,23,42,0.06),0_6px_18px_rgba(15,23,42,0.10)] hover:ring-[1.5px] hover:ring-sky-300 dark:hover:ring-[1.5px] dark:hover:ring-sky-400/50',
        className === 'dragging' &&
          'rotate-[1deg] shadow-[0_10px_30px_rgba(15,23,42,0.18)] ring-1 ring-sky-300 dark:ring-sky-400/50',
      )}
    >
      <div className="flex items-start gap-2">
        <Link
          to={claimPath}
          className={cn(
            'min-w-0 truncate text-[15px] font-bold leading-tight text-slate-800 no-underline dark:text-slate-100',
            !quiet && 'group-hover:text-sky-500 dark:group-hover:text-sky-300',
          )}
        >
          {card.userFirstName} {card.userLastName}
        </Link>
        {/* The handle stays mounted (the drag library needs it), but only
            people who can move claims see it. */}
        <span
          {...handleProps}
          data-keep-tile=""
          aria-hidden={!canDrag}
          aria-label="Drag"
          className={cn(
            '-mr-1 ml-auto shrink-0 text-slate-400 transition-opacity dark:text-slate-400',
            canDrag ? cn('cursor-grab opacity-50', !quiet && 'group-hover:opacity-100') : 'hidden',
          )}
        >
          <GripVertical size={15} />
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Tip
          quiet={quiet}
          id={`${card.claimId}-role`}
          className="min-w-0"
          text={
            needsCmFlag
              ? roleAssignedId
                ? `No Claims Manager assigned yet. Onboarding: ${roleDisplayName}.`
                : 'No Claims Manager assigned yet, and onboarding is unassigned too.'
              : roleAssignedId
                ? `${roleLabel} assigned to ${roleDisplayName}`
                : `${roleLabel} is unassigned.`
          }
        >
          <span
            className={cn(
              'inline-flex min-w-0 items-center gap-1.5 truncate text-xs',
              roleWarn ? 'font-semibold text-rose-600 dark:text-rose-300' : 'text-slate-500 dark:text-slate-400',
            )}
          >
            <RoleIcon size={12} className="shrink-0" />
            <span className="truncate">
              {needsCmFlag ? `Needs a CM · ${roleDisplayName}` : roleDisplayName}
            </span>
          </span>
        </Tip>
        {isAdmin ? (
          <Tip
            quiet={quiet}
            id={`${card.claimId}-value`}
            keep
            className="ml-auto shrink-0"
            text={`Contract potential value for ${card.claimCurrentRating}% - 100%`}
          >
            <span className="ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold tabular-nums text-slate-600 dark:text-slate-300">
              <FileText size={12} /> {financialDisplay}
            </span>
          </Tip>
        ) : null}
      </div>

      {/* No status line (Nic 2026-10-09): the column already says it. */}
      <div className="flex items-center gap-1.5 border-t border-slate-100 pt-2 dark:border-white/10">
        <Tip
          quiet={quiet}
          id={`${card.claimId}-update`}
          keep
          className="shrink-0"
          text={`Last updated ${daysSince} days ago on ${moment(card.claimUpdatedOn).format('MM/DD/YYYY')}`}
        >
          <span
            className={cn(
              'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
              stale
                ? 'bg-rose-50 text-rose-700 dark:!bg-rose-500/15 dark:text-rose-300'
                : 'bg-slate-100 text-slate-500 dark:!bg-white/10 dark:text-slate-400',
            )}
          >
            <CalendarDays size={11} />
            {moment(card.claimUpdatedOn).format('MMM D')}
          </span>
        </Tip>
        {/* Submissions, then the provider (Nic 2026-10-20). Everything here
            stays on this one line (the provider name truncates), so every
            tile is the same height with or without them. */}
        <Tip
          quiet={quiet}
          id={`${card.claimId}-submissions`}
          keep
          className="shrink-0"
          text={
            submissions.length
              ? `${submissions.length} submission${submissions.length === 1 ? '' : 's'}: ${submissions
                  .slice()
                  .reverse()
                  .map(submissionLine)
                  .join(', ')}`
              : 'No submissions yet'
          }
        >
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
              submissions.length
                ? 'bg-indigo-50 text-indigo-700 dark:!bg-indigo-500/15 dark:text-indigo-300'
                : 'bg-slate-100 text-slate-400 dark:!bg-white/10 dark:text-slate-500',
            )}
          >
            <Send size={11} />
            {submissions.length}
          </span>
        </Tip>
        {providerName ? (
          <Tip
            quiet={quiet}
            id={`${card.claimId}-provider`}
            keep
            className="min-w-0"
            text={`Provider: ${providerName}`}
          >
            <span className="inline-flex min-w-0 items-center gap-1 whitespace-nowrap rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-teal-700 dark:!bg-teal-500/15 dark:text-teal-300">
              <Stethoscope size={11} className="shrink-0" />
              <span className="truncate">{providerName}</span>
            </span>
          </Tip>
        ) : null}
        {/* In Process is the floor (Nic 2026-10-09): the CM's flag, and a
            send back the Onboarding Manager hasn't decided yet. */}
        {card.claimWaitingOnDocsAt ? (
          <Tip
            quiet={quiet}
            id={`${card.claimId}-waiting`}
            keep
            className="shrink-0"
            text={`Waiting on documents since ${moment(card.claimWaitingOnDocsAt).format('MMM D')}${
              card.claimWaitingOnDocsNote ? `: ${card.claimWaitingOnDocsNote}` : ''
            }`}
          >
            <span
              aria-label="Waiting on documents"
              className="inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-amber-700 dark:!bg-amber-500/15 dark:text-amber-300"
            >
              <Hourglass size={11} />
            </span>
          </Tip>
        ) : null}
        {showAddressMissingBadge ? (
          <Tip
            quiet={quiet}
            id={`${card.claimId}-address-missing`}
            keep
            className="shrink-0"
            text="Please add a verified address for this client before proceeding."
          >
            <span
              aria-label="Address missing"
              className="inline-flex items-center rounded-full bg-amber-400 px-1.5 py-0.5 text-slate-900"
            >
              <MapPin size={11} />
            </span>
          </Tip>
        ) : null}
        {onMeetingInvite && !NO_MEETING_STATUSES.includes(statusKey) ? (
          <Tip quiet={quiet} className="ml-auto shrink-0" text="Create Meeting Invite">
            <button
              type="button"
              aria-label="Create Meeting Invite"
              onClick={() => onMeetingInvite(card)}
              className="inline-flex items-center rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-sky-700 dark:text-slate-500 dark:hover:!bg-white/10 dark:hover:text-sky-300"
            >
              <CalendarPlus size={14} />
            </button>
          </Tip>
        ) : null}
      </div>
    </div>
  );
};

export default SalesCard;
